import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './Results.css';

interface User {
  id: string;
  email: string;
  role: string;
}

interface SimulationResult {
  id: string;
  transcript: string;
  analysis: {
    certification_minimum_analysis: {
      mentioned_all_cof_elements: boolean;
      asked_open_ended_questions: boolean;
      pivoted_on_price_objection: boolean;
    };
    performance_analysis: Array<{
      interaction_name: string;
      core_strength: string;
      best_practice_gap: string;
      actionable_recommendation: string;
    }>;
  };
  certification_status: string;
  completed_at: string;
  simulation: {
    name: string;
    description: string;
  };
}

interface ResultsProps {
  user: User;
}

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

const Results: React.FC<ResultsProps> = ({ user }) => {
  const { id } = useParams<{ id: string }>();
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadResults();
  }, [id]);

  const loadResults = async () => {
    try {
      const { data, error } = await supabase
        .from('simulation_results')
        .select(`
          *,
          simulation:simulations(name, description)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setResult(data);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCertificationStatus = () => {
    if (!result) return null;
    
    const cert = result.analysis.certification_minimum_analysis;
    const passedCriteria = [
      cert.mentioned_all_cof_elements,
      cert.asked_open_ended_questions,
      cert.pivoted_on_price_objection
    ].filter(Boolean).length;

    return {
      passed: passedCriteria >= 2, // Need at least 2 out of 3 criteria
      criteria: passedCriteria,
      total: 3
    };
  };

  const getBenchmarkMessage = () => {
    // This would be calculated based on historical data
    // For now, return a sample message
    return "top 20% of MedTech sales professionals";
  };

  if (loading) {
    return (
      <div className="results-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="results-container">
        <div className="error-state">
          <h2>Error Loading Results</h2>
          <p>{error || 'Results not found'}</p>
          <Link to="/dashboard" className="btn btn-primary">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const certificationStatus = getCertificationStatus();

  return (
    <div className="results-container">
      <div className="results-header">
        <h1>Simulation Results</h1>
        <p>{result.simulation.name}</p>
        <div className="completion-info">
          <span>Completed: {new Date(result.completed_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Certification Status */}
      <div className="certification-section">
        <div className={`certification-card ${certificationStatus?.passed ? 'certified' : 'not-certified'}`}>
          <div className="certification-header">
            <h2>
              {certificationStatus?.passed ? '🎉 Congratulations!' : '📚 Keep Learning!'}
            </h2>
            <div className={`certification-status ${result.certification_status}`}>
              {result.certification_status === 'certified' ? 'Certified' : 'Not Yet Certified'}
            </div>
          </div>
          
          <div className="certification-message">
            {certificationStatus?.passed ? (
              <p>
                You've successfully demonstrated the core competencies required for MedTech sales conversations. 
                Your performance ranks in the {getBenchmarkMessage()}.
              </p>
            ) : (
              <p>
                You're making good progress! Focus on the areas below to achieve certification 
                and join the {getBenchmarkMessage()}.
              </p>
            )}
          </div>

          <div className="certification-criteria">
            <h3>Certification Criteria</h3>
            <div className="criteria-list">
              <div className={`criterion ${result.analysis.certification_minimum_analysis.mentioned_all_cof_elements ? 'passed' : 'failed'}`}>
                <span className="criterion-icon">
                  {result.analysis.certification_minimum_analysis.mentioned_all_cof_elements ? '✓' : '✗'}
                </span>
                <span>Mentioned all COF elements (Clinical, Operational, Financial)</span>
              </div>
              <div className={`criterion ${result.analysis.certification_minimum_analysis.asked_open_ended_questions ? 'passed' : 'failed'}`}>
                <span className="criterion-icon">
                  {result.analysis.certification_minimum_analysis.asked_open_ended_questions ? '✓' : '✗'}
                </span>
                <span>Asked open-ended questions</span>
              </div>
              <div className={`criterion ${result.analysis.certification_minimum_analysis.pivoted_on_price_objection ? 'passed' : 'failed'}`}>
                <span className="criterion-icon">
                  {result.analysis.certification_minimum_analysis.pivoted_on_price_objection ? '✓' : '✗'}
                </span>
                <span>Pivoted to configuration on price objection</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Analysis */}
      <div className="performance-section">
        <h2>Performance Analysis</h2>
        <div className="performance-grid">
          {result.analysis.performance_analysis.map((analysis, index) => (
            <div key={index} className="performance-card">
              <h3>{analysis.interaction_name}</h3>
              
              <div className="analysis-item">
                <h4>🎯 Your Core Strength</h4>
                <p>{analysis.core_strength}</p>
              </div>
              
              <div className="analysis-item">
                <h4>📈 Growth Opportunity</h4>
                <p>{analysis.best_practice_gap}</p>
              </div>
              
              <div className="analysis-item recommendation">
                <h4>💡 Actionable Recommendation</h4>
                <p>{analysis.actionable_recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conversation Transcript */}
      <div className="transcript-section">
        <h2>Conversation Transcript</h2>
        <div className="transcript-container">
          <pre className="transcript-text">{result.transcript}</pre>
        </div>
      </div>

      {/* Actions */}
      <div className="results-actions">
        <Link to="/dashboard" className="btn btn-primary">
          Return to Dashboard
        </Link>
        <button 
          className="btn btn-secondary"
          onClick={() => window.print()}
        >
          Print Report
        </button>
      </div>
    </div>
  );
};

export default Results;
